"use client";

import {
	useEffect,
	useMemo,
	useRef,
	type CSSProperties,
	type ReactNode,
} from "react";

import { useInViewStore } from "@/store/inViewStore";

import { cn } from "@/utils";

type SectionProps = {
	id: string;
	label: string;
	order: number;
	children: ReactNode;
	className?: string;
	style?: CSSProperties;
	fullWidth?: boolean;
};

const intersectionThreshold = Array.from({ length: 11 }, (_, index) => index / 10);

export function Section({
	id,
	label,
	order,
	children,
	className,
	style,
  fullWidth = false,
}: SectionProps) {
	const registerSection = useInViewStore((state) => state.registerSection);
	const unregisterSection = useInViewStore((state) => state.unregisterSection);
	const updateSectionVisibility = useInViewStore(
		(state) => state.updateSectionVisibility,
	);

	const sectionRef = useRef<HTMLElement>(null);

	useEffect(() => {
		registerSection({ id, label, order });
		return () => {
			unregisterSection(id);
		};
	}, [id, label, order, registerSection, unregisterSection]);

	const observerOptions = useMemo(
		() => ({
			rootMargin: "-25% 0px -25% 0px",
			threshold: intersectionThreshold,
		}),
		[],
	);

	useEffect(() => {
		const node = sectionRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(([entry]) => {
			updateSectionVisibility({
				id,
				inView: entry.isIntersecting,
				intersectionRatio: entry.intersectionRatio,
				bounds: {
					top: entry.boundingClientRect.top,
					height: entry.boundingClientRect.height,
				},
			});
		}, observerOptions);

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, [id, observerOptions, updateSectionVisibility]);

	return (
		<section
			ref={sectionRef}
			id={id}
			data-section-id={id}
			className={cn(
        "sm:px-16 px-6 sm:py-16 py-10 mx-auto relative scroll-mt-32",
        className,
        fullWidth ? "max-w-full" : "max-w-7xl",
      )}
			style={{
        ...style,
        maxWidth: fullWidth ? "100%" : "1200px",
      }}
		>
			{children}
		</section>
	);
}

